<template>
    <div
        class="timer-card"
        v-longpress="onLongPress"
        v-press="onPress"
        :class="{
            selected: selected,
            ['status-' + timer.status]: true,
            active: timer.status > 0,
            disabled: disabled
        }"
        :style="{ pointerEvents: disabled ? 'none' : 'auto', cursor: disabled ? 'wait' : 'pointer' }"
    >
        <div class="rel-status" :class="'status-' + (timer.data.color_relation || 0)">
            {{ timer.data.letter_relation || '' }}
        </div>

        <header>
            <img class="img" :src="productImage" alt="" />
        </header>

        <div class="bottom-section">
            <span class="text">
                <span class="product-name">{{ productName }}</span><span v-if="timer.data.package" class="package-text"> {{ timer.data.package }}</span>
            </span>
            <div class="time" :class="{ 'time-days': isLive && timeLeft.d !== 0 }">
                <template v-if="isLive && timeLeft.d === 0">
                    {{ timeLeft.h }}:{{ timeLeft.m }}<span class="seconds">:{{ timeLeft.s }}</span>
                </template>
                <template v-else-if="isLive && timeLeft.d !== 0">
                    {{ timeLeft.d }} {{ $tc('timersproduct.day', timeLeft.d) }}
                </template>
                <template v-else>00:00</template>
            </div>
        </div>

        <div v-if="selected" class="select-timer">
            <div class="corner-flag">
                <span class="checkmark"></span>
            </div>
            <div class="border-white"></div>
        </div>

        <span v-if="showQuantityBadge" class="quantity-avaible">
            {{ quantityAvailable }}
        </span>
    </div>
</template>

<script>
import { TIMERPRODUCT_STATUS } from '@/constants'

export default {
    name: 'TimerGroupModalCard',
    props: {
        timer: {
            type: Object,
            required: true
        },
        selected: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        productImage: {
            type: String,
            default: ''
        },
        productName: {
            type: String,
            default: ''
        },
        now: {
            type: Object,
            required: true
        }
    },
    computed: {
        isLive() {
            return this.timer.status !== TIMERPRODUCT_STATUS.EXPIRED
        },
        showQuantityBadge() {
            return Number(this.timer.data.quantity_avaibles) > 1
        },
        quantityAvailable() {
            return Number(this.timer.data.quantity_avaibles) || Number(this.timer.data.quantity)
        },
        timeLeft() {
            const now = this.now
            let refDate = this.timer.data.date_timestamp

            if (this.timer.status === TIMERPRODUCT_STATUS.PREPARATION) {
                refDate = this.timer.data.prep_date_timestamp
            }

            const duration = moment.duration(moment(refDate * 1000).diff(now))
            let diff = duration.asSeconds()

            let diffDays = parseInt(diff / 86400, 10)
            if (diffDays < 3) {
                diffDays = 0
            }
            diff = diff - diffDays * 86400
            const diffHours = String(parseInt(diff / 3600, 10)).padStart(2, '0')
            diff = diff - diffHours * 3600
            const diffMinutes = String(parseInt(diff / 60, 10)).padStart(2, '0')
            diff = diff - diffMinutes * 60
            const diffSeconds = String(parseInt(diff, 10)).padStart(2, '0')

            return {
                d: diffDays,
                h: diffHours,
                m: diffMinutes,
                s: diffSeconds
            }
        }
    },
    methods: {
        onPress() {
            if (!this.disabled) {
                this.$emit('select')
            }
        },
        onLongPress() {
            if (!this.disabled) {
                this.$emit('longpress')
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.timer-card {
    padding: 0 2px;
    overflow: hidden;
    min-width: 0;
    // Alto total de la tarjeta: imagen + nombre (3 líneas) + reloj (igual que timer.vue).
    height: 150px;
    background-color: $color-error-500;
    border-radius: 4px;
    position: relative;
    cursor: pointer;
    // Distribución en columna: imagen → nombre → tiempo (mismo patrón que timer.vue / TimerGroupCard.vue).
    display: flex;
    flex-direction: column;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(.disabled) {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &.disabled {
        opacity: 0.6;
        cursor: wait !important;
        pointer-events: none !important;
    }

    &.selected {
        box-shadow: 0 0 0 3px $color-info-500;
    }

    &.status-0 {
        background-color: $color-error-500;
    }
    &.status-1 {
        background-color: $color-secondary-500;
    }
    &.status-2 {
        background-color: $color-success-500;
    }
    &.status-3 {
        background-color: $color-warning-500;
    }

    .rel-status {
        width: 24px;
        height: 24px;
        border: 1px solid $color-white;
        background-color: $color-primary-300;
        position: absolute;
        top: 5px;
        left: 5px;
        border-radius: 100%;
        @include display-flex();
        @include justify-content(center);
        @include align-items(center);
        color: $color-white;
        font-size: 14px;
        line-height: normal;
        font-weight: 700;
        font-style: normal;

        &.status-1 { background-color: $color-primary-300; }
        &.status-2 { background-color: $color-primary-500; }
        &.status-3 { background-color: $color-primary-800; }
        &.status-4 { background-color: $color-secondary-500; }
        &.status-5 { background-color: $color-secondary-800; }
        &.status-6 { background-color: $color-neutral-600; }
        &.status-7 { background-color: $color-warning-800; }
        &.status-8 { background-color: $color-success-800; }
        &.status-9 { background-color: $color-info-500; }
        &.status-10 { background-color: $color-error-800; }
    }

    .quantity-avaible {
        position: absolute;
        top: 5px;
        right: 5px;
        width: 24px;
        height: 24px;
        color: $color-white;
        text-align: center;
        font-family: $text-bold;
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        z-index: 5;
    }

    header {
        // La imagen queda anclada arriba (no crece), agrupada con el nombre.
        flex: 0 0 auto;
        width: 100%;
        text-align: center;
        padding: 5px;
        min-height: auto;

        .img {
            width: 35px;
            height: 35px;
            display: block;
            margin: 0 auto;
        }
    }

    .bottom-section {
        @include timer-bottom-section;
        // Ocupa el espacio bajo la imagen y reparte: nombre arriba (pegado a la
        // imagen) y reloj anclado abajo, evitando que el texto invada la imagen
        // o que el reloj quede recortado con nombres de hasta 3 líneas.
        flex: 1 1 auto;
        min-height: 0;
        @include justify-content(space-between);
    }

    .time {
        min-height: 1.2em;
        // El reloj nunca se parte en varias líneas ni se recorta (igual que
        // timer.vue / TimerGroupCard.vue), incluso con nombres de 3 líneas.
        white-space: nowrap;

        // Formato en días (p. ej. "3 días"): puede ser más ancho que la
        // tarjeta, así que permitimos el salto de línea para no recortar el
        // valor (el reloj numérico sigue en una sola línea).
        &.time-days {
            white-space: normal;
            line-height: 1.1;
        }
    }

    .select-timer {
        background-color: rgba(238, 229, 229, 0.3);
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        z-index: 10;
        border: 4px solid $color-info-500;
        pointer-events: none;

        .border-white {
            width: 100%;
            height: 100%;
            border: 4px solid $color-white;
        }

        .corner-flag {
            position: absolute;
            top: 0;
            right: 0;
            width: 0;
            height: 0;
            border-top: 50px solid $color-info-500;
            border-left: 50px solid transparent;
        }

        .checkmark {
            background-image: img('checked_timer.svg');
            background-size: contain;
            background-repeat: no-repeat;
            width: 24px;
            height: 24px;
            position: absolute;
            top: -45px;
            right: 0px;
            font-size: 24px;
        }
    }
}
</style>
